<template>
<div>
  <my-header></my-header>
  
  <div class="content-container-level-1">
    <h2 class="mb-3">Платежи</h2>

    <table class="mb-2">
      <tr>
        <th class="pr-1">
          <label for="shop-selector" class="mb-0">Магазин</label>
        </th>
        <td>
          <b-form-select
            id="shop-selector"
            v-model="selectedShop"
            :options="shopOptions"
          />
        </td>
      </tr>
    </table>

    <p v-if="message !== null">
      {{message}}
    </p>

    <template v-if="isDataLoaded && billing.length > 0">
      <table class="table table-bordered billing-table">
        <thead class="thead-dark">
          <tr class="text-center">
            <th colspan="4">Bill</th>
            <th colspan="9">Player</th>
            <th colspan="7">Service</th>
          </tr>
          <tr>
            <th scope="col">Id</th>
            <th scope="col"><span id="col-n">N</span></th>
            <th scope="col">Time</th>
            <th scope="col">Shop</th>
            <th scope="col">Player</th>
            <th scope="col">Client</th>
            <th scope="col">RFM</th>
            <th scope="col">
              <span v-b-popover.hover.top="'Уровень (level)'">
                Lev
              </span>
            </th>
            <th scope="col">
              <span v-b-popover.hover.top="'Платформа (platform)'">
                Platf
              </span>
            </th>
            <th scope="col">
              <!-- Since reg -->
              <span v-b-popover.hover.top="'Время с момента регистрации игрока'">
                Age
              </span>
            </th>
            <th scope="col" colspan="2" class="text-center">
              <span v-b-popover.hover.top="'Монеты. Покупка (paid) / бонус (bonus)'">
                Coins p/b
              </span>
            </th>
            <th scope="col">Gross</th>
            <th scope="col">
              <span v-b-popover.hover.top="'Таблица (table)'">
                T
              </span>
            </th>
            <th scope="col">Service</th>
            <th scope="col">Price USD</th>
            <th scope="col">Curr</th>
            <th scope="col">Coins</th>
            <th scope="col">Products</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in billing"
            :key="item.Id"
          >
            <th scope="row" class="text-right">{{ item.Id }}</th>
            <td class="text-right">{{ item.PaymentsCount }}</td>
            <td>{{ item.Ctime | getMomentStr }}</td>
            <td>
              <span v-b-popover.hover.top="getShopName(item.Shop)">
                {{ item.Shop | getShortShopName }}
              </span>
            </td>
            <td>
              <router-link :to="{name: 'player_info', params: {playerId: item.PlayerId}}">{{item.PlayerId}}</router-link>
            </td>
            <td>{{ item.ClientVersion | getOptionalStringDefaultStr('?') }}</td>
            <td>{{ item.Rfm | getPlayerRFMCategoryNameShort }}</td>
            <td class="text-right">{{ item.PlayerLevel | getOptionalNumberStr('?') }}</td>
            <td>{{ item.Platform | getPlatformName }}</td>
            <td>{{ getDurationSincePlayerRegTimeStr(item) }}</td>
            <td class="text-right">{{ item.GemsPaid }}</td>
            <td class="text-right">{{ item.GemsBonus }}</td>
            <td class="text-right">{{ item.PlayerGrossCent | convertCentToString }}</td>
            <td>
              <span v-b-popover.hover.top="convertBillingTableAliasToName(item.TableId)">
                {{ item.TableId | convertBillingTableAliasToAbbr }}
              </span>
            </td>
            <td>{{ item.Service | getShortBillingServiceId }}</td>
            <td class="text-right">
              {{ item.PriceCent | convertCentToString }}
            </td>
            <td>{{ item.UserCurrency }}</td>
            <td class="text-right">{{ '+' + item.Gems }}</td>
            <td>
              <span
                v-if=isProductsButtonVisible(item.TableId)
                :id="getProductsButtonId(item.Id)"
                class="tooltip-button products-button"
                @click='handleProductsClick(item)'
              >
                products
              </span>
            </td>
          </tr>
        </tbody>
      </table>

      <b-tooltip target="col-n" triggers="hover">
        Player payments count<br>
        (including current payment)
      </b-tooltip>
    </template>

    <loading-indicator v-if="isInitialLoadingIndicatorVisible"/>

    <loading-indicator-const-height
      id="additional-loading-indicator"
      :useDelay="false"
      :class="{invisible: !isAdditionalBlockLoading}"
    />

    <template v-if="isProductsPopoverShowing">
      <b-popover
        show
        :target="productsButtonId"
        placement="top"
        triggers="manual"
      >
        <billing-products-table
          :billingRecord="currentItem"
          :productGroups="currentBillingItemInfoProducts"
        />
      </b-popover>
    </template>

  </div>
</div>
</template>

<script>
import { HTTP } from '../http-common.js';
import notifier from '../notifier.js';
import MyHeader from './Header.vue';
import LoadingIndicator from './LoadingIndicator.vue';
import LoadingIndicatorConstHeight from './LoadingIndicatorConstHeight.vue';
import BillingProductsTable from './BillingProductsTable.vue';
import { getDurationStrForBilling } from '../util.js';
import { addParamToQueryObject    } from '../util.js';
import { convertCentToString } from '../money.js';

/**
 * @typedef { import('../model/billing.js').Shop } Shop
 */

import { addTableFragmentObjectToTableMap } from '../model/billing.js';
import { findBillingItemInfoProducts      } from '../model/billing.js';
import { getShopName                      } from '../model/billing.js';
import { getShortShopName                 } from '../model/billing.js';
import { getShopOptionsForFilter          } from '../model/billing.js';
import { convertBillingTableAliasToName   } from '../model/billing.js';
import { convertBillingTableAliasToAbbr   } from '../model/billing.js';
import { getOptionalShopQueryName         } from '../model/billing.js';

import { isProductsButtonVisible } from '../view/billing.js';
import { mustHidePopover         } from '../view/billing.js';

export default {
  name: 'Billing',

  components: {
    MyHeader,
    LoadingIndicator,
    LoadingIndicatorConstHeight,
    BillingProductsTable,
  },

  props: {
    shop: {
      type: Number,
    }
  },

  data() {
    return {
      isInitialBlockLoading: false,
      isAdditionalBlockLoading: false,
      billing: null,
      regTimeMap: new Map(),
      tableMap: new Map(),

      productsButtonId: null,
      isProductsPopoverShowing: false,
      currentItem: null,

      //-----------------------------------------------------------------------
      // filter
      //-----------------------------------------------------------------------

      shopOptions: getShopOptionsForFilter(),

      /** @type {?Shop} */
      selectedShop: this.shop,

      //-----------------------------------------------------------------------
    };
  },

  computed: {
    /** @returns {boolean} */
    isInitialLoadingIndicatorVisible() {
      // Если при загрузке начального блока в переменной billing уже есть данные,
      // то загрузка происходит из-за изменения фильтра пользователем.
      // В этом случае не нужно показывать индикатор начальной загрузки.
      return this.isInitialBlockLoading && this.billing === null;
    },

    /** @returns {boolean} */
    isLoading() {
      return this.isInitialBlockLoading || this.isAdditionalBlockLoading;
    },

    /** @returns {boolean} */
    isDataLoaded() {
      return this.billing !== null;
    },

    /** @returns {?string} */
    message() {
      if(this.billing !== null && this.billing.length === 0) {
        return 'No billing records.';
      }
      return null;
    },

    currentBillingItemInfoProducts() {
      return findBillingItemInfoProducts(this.tableMap, this.currentItem);
    },
  },

  created() {
    this.loadInitialBlock();
  },

  mounted() {
    document.addEventListener('keydown', this.escapeKeyHandler);
    document.body.parentNode.addEventListener('mousedown', this.globalMousedownHandler);
  },

  beforeDestroy() {
    this.removeScrollHandler();
    document.removeEventListener('keydown', this.escapeKeyHandler);
    document.body.parentNode.removeEventListener('mousedown', this.globalMousedownHandler);
  },

  watch: {
    shop() {
      this.loadInitialBlock();
    },

    selectedShop(newShop) {
      const queryObject = {};
      const shopQueryName = getOptionalShopQueryName(newShop);
      addParamToQueryObject(queryObject, 'shop', shopQueryName);
      this.$router.push({name: 'billing', query: queryObject});
    },
  },

  methods: {
    loadInitialBlock() {
      this.isInitialBlockLoading = true;
      this.removeScrollHandler();

      const requestConf = {
        params: {}
      };

      const shop = this.shop;
      if(shop !== null) {
        requestConf.params.shop = shop;
      }

      HTTP.get('/billing/last_block', requestConf)
      .then(response => {
        this.billing = [];
        this.processBlock(response.data);
        this.isInitialBlockLoading = false;
      })
      .catch(err => {
        this.isInitialBlockLoading = false;
        notifier.showError(err, {sender: this});
      });
    },

    loadAdditionalBlock() {
      this.isAdditionalBlockLoading = true;
      this.removeScrollHandler();

      const item = this.billing[this.billing.length - 1];

      const requestConf = {
        params: {
          item_id: item.Id
        }
      };

      const shop = this.shop;
      if(shop !== null) {
        requestConf.params.shop = shop;
      }

      HTTP.get('/billing/block_before_item', requestConf)
      .then(response => {
        this.processBlock(response.data);
        this.isAdditionalBlockLoading = false;
      })
      .catch(err => {
        this.isAdditionalBlockLoading = false;
        notifier.showError(err.response.data, {sender: this});
      });
    },

    processBlock(data) {
      for(const playerIdStr in data.regTimeMap) {
        const playerId = parseInt(playerIdStr, 10);
        const regTime = data.regTimeMap[playerIdStr];
        this.regTimeMap.set(playerId, regTime);
      }

      addTableFragmentObjectToTableMap(this.tableMap, data.billingTableInfoMap);

      for(const item of data.items) {
        this.billing.push(item);
      }

      if(data.hasPreviousBlock) {
        this.addScrollHandler();
      }
    },

    windowScrollHander() {
      if(this.isLoading) {
        return;
      }

      const viewportHeight = document.documentElement.clientHeight;
      const windowBottom = document.documentElement.getBoundingClientRect().bottom;

      const SCROLL_DISTANCE = 1000;
      const isWindowBottomReached = windowBottom < viewportHeight + SCROLL_DISTANCE;
      if(isWindowBottomReached) {
        this.loadAdditionalBlock();
      }
    },

    addScrollHandler() {
      window.addEventListener('scroll', this.windowScrollHander);
    },

    removeScrollHandler() {
      window.removeEventListener('scroll', this.windowScrollHander);
    },

    getDurationSincePlayerRegTimeStr(billingRecord) {
      const regTime = this.regTimeMap.get(billingRecord.PlayerId);
      if(regTime === undefined) {
        return 'No data';
      }

      const duration = billingRecord.Ctime - regTime;
      return getDurationStrForBilling(duration);
    },

    isProductsButtonVisible,

    handleProductsClick(item) {
      if(!this.isProductsPopoverShowing) {
        this.currentItem = item;
        const productsButtonId = this.getProductsButtonId(item.Id);
        this.productsButtonId = productsButtonId;
        this.isProductsPopoverShowing = true;
        return;
      }

      if(this.isProductsPopoverShowing && item === this.currentItem) {
        this.stopProductsPopoverShowing();
        return;
      }

      if(this.isProductsPopoverShowing && item !== this.currentItem) {
        this.isProductsPopoverShowing = false;
        this.currentItem = item;
        this.productsButtonId = this.getProductsButtonId(item.Id);
        this.$nextTick(function() {
          this.isProductsPopoverShowing = true;
        });
        return;
      }
    },

    getProductsButtonId(billingRecordId) {
      return 'btn-' + billingRecordId + '-products';
    },

    escapeKeyHandler(event) {
      if(event.keyCode === 27 && this.isProductsPopoverShowing) {
        this.stopProductsPopoverShowing();
      }
    },

    globalMousedownHandler(event) {
      if(mustHidePopover(event.target)) {
        this.stopProductsPopoverShowing();
      }
    },

    stopProductsPopoverShowing() {
      this.isProductsPopoverShowing = false;
      this.productsButtonId = null;
      this.currentItem = null;
    },

    getShopName,
    convertBillingTableAliasToName,
  },

  filters: {
    getShortShopName,
    convertCentToString,
    convertBillingTableAliasToAbbr,
  },
}
</script>

<style scoped>
  .table {
    white-space: nowrap;
  }

  #additional-loading-indicator {
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
  }

  .tooltip-button {
    text-decoration: underline;
    cursor: pointer;
  }

  .popover {
    max-width: 800px;
  }
</style>

